import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { AdditionalOrderForm as Form } from './AdditionalOrderForm';
import { generateOptions, generateUrl } from './utils';
import { Wrapper } from './styles';
import { modifyUrlParams, urlParam } from '../../lib/url';
import { addMultiOrder, multiOrderError, multiOrderErrorReset } from '../../actions/multiOrders';
import { useMultiOrder } from '../Items/hooks/useMultiOrder';

const mapStateToProps = (state) => {
  const {
    page: { browserFingerprint, productType, locale, giftOrder, initialPageLoadParams, overrideCode },
    landingPage: { landingForm },
    retailerSettings: { name: retailerMoniker = '' },
    multiOrders: { orders, error, enabled },
    orderInfo,
    language,
    returnsSettings: {
      returns: { multi_order: { order_limit: settingOrderLimit = null } = {} } = {},
    } = {},
  } = state;

  const stateSecondFactor = Object.entries(landingForm).reduce(
    (acc, [key, value]) =>
      key.includes('email') || key.includes('zip')
        ? { ...acc, [key === 'zip' ? 'bzip' : key]: value }
        : acc,
    {},
  );

  return {
    getUrl: generateUrl({
      stateSecondFactor,
      overrideCode,
      productType,
      locale,
      giftOrder,
      initialPageLoadParams,
      retailerMoniker,
      version: '3',
    }),
    options: generateOptions({ browserFingerprint }),
    twoFaType: Object.keys(landingForm).includes('zip') ? 'bzip' : 'email',
    orders,
    errorMessage: error,
    orderInfo,
    language,
    settingOrderLimit,
    multiOrderEnabled: enabled,
  };
};

const FormComponent = ({
  getUrl,
  options,
  dispatch,
  errorMessage,
  twoFaType,
  updateAdditionalOrders,
  orders,
  orderInfo,
  language,
  settingOrderLimit,
}) => {
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(0);
  const { allOrders } = useMultiOrder();

  const errorReset = () => dispatch(multiOrderErrorReset());

  const onSubmit = ({ orderNumber, secondFactorInput }) => {
    setLoading(true);
    errorReset();
    if (limit !== 0 && allOrders.length >= limit) {
      dispatch(multiOrderError(language['returns.items.multi_item_form.over_limit_error']));
      setLoading(false);
      return;
    }
    fetch(
      getUrl({
        orderNumber,
        secondFactorInput:
          !secondFactorInput || !secondFactorInput[twoFaType] ? null : secondFactorInput,
      }),
      {
        ...options,
        headers: {
          ...options.headers,
          'narvar-multi-order': 'true',
          'additional-orders':
            allOrders.map(({ order_info: { order_number: o } }) => o).join(';') || '',
        },
      },
    )
      .then((res) => res.json())
      .then((data) => {
        const { error_response: { error_code: errorCode, error_message: errorMsg } = {} } = data;
        const error = errorMsg || errorCode;

        if (error) {
          dispatch(multiOrderError(error));
          throw new Error(error);
        }

        const allExistingOrders = [
          orderInfo.orderData.order_info.order_number,
          ...orders.map((o) => o.order_info.order_number),
        ];

        if (allExistingOrders.includes(data.order_info.order_number)) {
          const existingItemError = language['returns.items.multi_item_form.existing_order_error'];
          dispatch(multiOrderError(existingItemError));
          throw new Error(existingItemError);
        }

        dispatch(addMultiOrder(data));

        updateAdditionalOrders();
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  };

  useEffect(() => {
    (async () => {
      const orderLimit = settingOrderLimit || 0;
      setLimit(orderLimit);

      const additionalOrders = urlParam({ param: 'additionalOrders' });
      if (additionalOrders) {
        let orderNumbers = additionalOrders.split(',');
        if (orderLimit !== 0) {
          orderNumbers = orderNumbers.slice(0, orderLimit - 1);
        }
        orderNumbers.forEach((orderNumber) => {
          onSubmit({ orderNumber });
        });
        modifyUrlParams({ paramsToDelete: ['additionalOrders'] });
      }
    })();
    return () => {};
  }, []);

  return (
    <Wrapper>
      <Form
        onSubmit={onSubmit}
        errorMessage={errorMessage}
        errorReset={errorReset}
        loading={loading}
        twoFaType={twoFaType}
      />
    </Wrapper>
  );
};

const Root = (props) => {
  const { multiOrderEnabled } = props;

  return multiOrderEnabled || urlParam({ param: 'multiOrder' }) ? (
    <FormComponent {...props} />
  ) : null;
};

export const AdditionalOrderForm = connect(mapStateToProps)(Root);

FormComponent.propTypes = {
  getUrl: PropTypes.func.isRequired,
  options: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  twoFaType: PropTypes.string.isRequired,
  updateAdditionalOrders: PropTypes.func.isRequired,
  orders: PropTypes.array.isRequired,
  orderInfo: PropTypes.object.isRequired,
  language: PropTypes.object.isRequired,
  settingOrderLimit: PropTypes.number,
};

FormComponent.defaultProps = {
  errorMessage: '',
  settingOrderLimit: null,
};
